import withErrorBoundary from "src/components/errorBoundary/withErrorBoundary";
import { IntegrationCapability, IntegrationCapabilityList } from 'src/types/settings/dashboard'
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import _ from "lodash";
import DOMPurify from 'dompurify';
import cx from "classnames";
import styles from "./styles.module.scss";
import UserAvatar from "../components/userAvatar";
import Actions from "src/components/actionbar";
import IntegrationCard from "../components/integrationCard";
import { useCapabilitiesFetch } from "src/query/settings/dashboard";
import { ActionNeeded, VersionUpdate } from "../components/smartCards";
import Slider from 'src/components/slider';
import Button from 'src/components/button';
import { useTinyMce } from "src/components/useTinyMce";
import InputGroup from "src/components/inputGroup";
import { ProfileEnum, profileDetails, resetPassword, resetPasswordErrorLabels, applicationPlatformTypes , externalLinkTypes , CapabilityCategoriesEnum , CapabilityPlatformTypesEnum  , externalLinksMapping, applicationQRCodeMapping, adminOnlyCapabilityTypes, newVersionAvailableInfo, CountryCodeEnum, SmsVerificationResponse } from "src/types/settings/profile";
import StackProgressBar from "../components/stackProgressbar";
import { useEmailSignatureUpdate, usePasswordUpdate, useUserFetch, useUserUpdate, useCapabilitiesActionNeededFetch, useActivatedKeysFetch, useSmsVerificationSend, usePhoneVerificationStatus } from "src/query/settings/profile";
import { useIntegrationsQuery } from "src/query/settings/integrations";
import { getIntegrationPath } from 'src/utils/util';
import AssetIconEditor from "src/legacy/components/AssetIconEditor/view";
import GenerateQRCode from "src/components/generateQRCode";
import { toast } from "react-toastify";
import Toast from "src/components/toast";
import { useProfileAdoption } from "src/query/settings/adoption/profile";
import { useDispatch } from "react-redux";
import { fetchUserProfile } from "src/redux/actions/users";
import IntegrationValidateView from "src/legacy/pages/settings/integrations/integrations/IntegrationConfirmView";
import SlidePaneView from "src/legacy/components/SlidePaneView";
import subscriptionPlanService from "src/utils/subscriptionPlan.service";
import userRoleService from "src/utils/userRoles.service";
import { stripCountryCode, isValidMobileNumber } from "src/utils/helper";
import PhoneVerificationInput from "src/components/phoneVerificationInput";

const NoIntegrations = '/images/settings/no_integrations.svg';
const emptySignature = '/images/settings/empty_email_signature.svg';
const BrowserControlBar = '/images/settings/browser_control_bar.png';
const emptyMobileNumber = '/images/coaching/mobile_verify.svg';

/** Backend code when this mobile is already verified on another user account */
const PHONE_NUMBER_ALREADY_ASSIGNED_CODE = "phone.number_already_assigned";
const MOBILE_DUPLICATE_USER_MESSAGE =
  "This mobile number is already registered to another user. Please use a different number.";

/** Backend code when SMS delivery fails */
const SMS_DELIVERY_FAILED_CODE = "phone.sms_delivery_failed";
const SMS_DELIVERY_FAILED_MESSAGE = "Failed to send verification link. Please try again.";

/** Session key: set only after Verify/SMS succeeds — not when saving mobile via Confirm */
const PHONE_VERIFICATION_PENDING_SESSION_KEY = "paperflite_phone_verification_pending";

const clearPhoneVerificationPending = () => {
  try {
    sessionStorage.removeItem(PHONE_VERIFICATION_PENDING_SESSION_KEY);
  } catch {
    // Private browsing / blocked storage
  }
};

const setPhoneVerificationPending = (compositeKey: string) => {
  try {
    sessionStorage.setItem(PHONE_VERIFICATION_PENDING_SESSION_KEY, compositeKey);
  } catch {
    // Private browsing / blocked storage
  }
};

const getPhoneVerificationPending = (): string | null => {
  try {
    return sessionStorage.getItem(PHONE_VERIFICATION_PENDING_SESSION_KEY);
  } catch {
    return null;
  }
};

/** E.164-style digits for comparison (+91XXXXXXXXXX stored as 919XXXXXXXXX) */
const toFullPhoneDigits = (countryCode: string, phone: string): string => {
  const ccDigits = countryCode.replace(/\D/g, "");
  const phoneDigits = phone.replace(/\D/g, "");
  if (!phoneDigits) return "";
  if (phoneDigits.startsWith(ccDigits) && phoneDigits.length > ccDigits.length) {
    return phoneDigits;
  }
  return `${ccDigits}${phoneDigits}`;
};

/** Local digits (without country code) from a composite key like +919513850684 */
const getLocalDigitsForCountry = (compositeKey: string, countryCode: string): string => {
  const fullDigits = compositeKey.replace(/\D/g, "");
  const ccDigits = countryCode.replace(/\D/g, "");
  if (fullDigits.startsWith(ccDigits) && fullDigits.length > ccDigits.length) {
    return fullDigits.slice(ccDigits.length);
  }
  return fullDigits.slice(-10);
};

/** Map of known error codes to user-friendly messages */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  "phone.sms_delivery_failed": SMS_DELIVERY_FAILED_MESSAGE,
  "phone.number_already_assigned": MOBILE_DUPLICATE_USER_MESSAGE,
  "phone.invalid_number": "The phone number entered is invalid. Please check and try again.",
  "phone.verification_expired": "The verification link has expired. Please request a new one.",
  "phone.verification_already_used": "This verification link has already been used.",
};

/** Check if a message looks like a raw error code (not user-friendly) */
function isRawErrorCode(message?: string): boolean {
  if (!message) return false;
  // Matches patterns like "phone.sms_delivery_failed", "error.something", etc.
  return /^[a-z]+\.[a-z_]+$/i.test(message.trim());
}

/** Get user-friendly message for an error code, or return the message if it's already friendly */
function getUserFriendlyErrorMessage(code?: string, message?: string, fallback?: string): string {
  // First check if we have a mapped message for this code
  if (code && ERROR_CODE_MESSAGES[code]) {
    return ERROR_CODE_MESSAGES[code];
  }
  // If the message looks like a raw error code, use the fallback
  if (isRawErrorCode(message)) {
    // Try to find a mapped message for the message-as-code
    if (message && ERROR_CODE_MESSAGES[message]) {
      return ERROR_CODE_MESSAGES[message];
    }
    return fallback || "An error occurred. Please try again.";
  }
  // Otherwise return the message or fallback
  return message || fallback || "An error occurred. Please try again.";
}

/** Paperflite APIs often return `{ errors: [{ code, message }] }`; protobuf JSON may use `errorsList`. */
function extractPaperfliteErrorPayload(error: unknown): { code?: string; message?: string } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;
  
  // Check multiple possible locations for the error data
  const data = err?.response?.data || err;
  
  if (!data || typeof data !== "object") {
    return {};
  }
  
  // Try different possible array keys for error list
  const errorArray = data.errors || data.errorsList || data.errorMessages;
  
  if (Array.isArray(errorArray) && errorArray.length > 0) {
    const firstError = errorArray[0];
    return {
      code: firstError?.code,
      message: firstError?.message,
    };
  }
  
  // Fallback: maybe it's a single error object
  if (data.code || data.message) {
    return { code: data.code, message: data.message };
  }
  
  return {};
}

function isPhoneNumberAlreadyAssignedError(code?: string, message?: string): boolean {
  if (code === PHONE_NUMBER_ALREADY_ASSIGNED_CODE) {
    return true;
  }
  if (code && code.includes("number_already_assigned")) {
    return true;
  }
  if (/already verified on another account/i.test(message ?? "")) {
    return true;
  }
  return false;
}

export const defaultManagePassword = {
  [ProfileEnum.oldPassword]:'',
  [ProfileEnum.newPassword1]: '',
  [ProfileEnum.newPassword2]: '',
}

const doesMaskedNumberMatchPhone = (maskedNumber?: string, phoneNumber?: string): boolean => {
  const rawPhoneDigits = (phoneNumber ?? "").replace(/\D/g, "");
  const phoneDigits = stripCountryCode(rawPhoneDigits);

  if (!phoneDigits) {
    return false;
  }

  const normalizedMask = (maskedNumber ?? "").trim();
  if (!normalizedMask) {
    return true;
  }

  // Remove leading country code section like "+91 " to compare only local number mask.
  const localMask = normalizedMask.replace(/^\+\d+\s*/, "");
  const maskStartIndex = localMask.search(/[Xx*]/);

  // If no masking placeholders are present, fallback to trailing digit match.
  if (maskStartIndex < 0) {
    const maskedDigits = localMask.replace(/\D/g, "");
    if (!maskedDigits) {
      return true;
    }
    const compareLength = Math.min(4, maskedDigits.length, phoneDigits.length);
    return phoneDigits.slice(-compareLength) === maskedDigits.slice(-compareLength);
  }

  const visiblePrefix = localMask.slice(0, maskStartIndex).replace(/\D/g, "");
  const visibleSuffix = localMask.slice(maskStartIndex).replace(/\D/g, "");

  if (visiblePrefix && !phoneDigits.startsWith(visiblePrefix)) {
    return false;
  }
  if (visibleSuffix && !phoneDigits.endsWith(visibleSuffix)) {
    return false;
  }

  // If mask had placeholders but no visible digits, don't block verified state.
  return true;
};

const hasMaskedDigits = (maskedNumber?: string): boolean => {
  return (maskedNumber ?? "").replace(/\D/g, "").length > 0;
};

const detectCountryCodeFromPhone = (mobilePhone?: string): CountryCodeEnum => {
  if (!mobilePhone) {
    return CountryCodeEnum.India;
  }
  const digitsOnly = mobilePhone.replace(/[\s\-\(\)\+]/g, "");
  if (digitsOnly.startsWith("91") && digitsOnly.length === 12) {
    return CountryCodeEnum.India;
  }
  if (digitsOnly.startsWith("1") && digitsOnly.length === 11) {
    return CountryCodeEnum.UnitedStates;
  }
  return CountryCodeEnum.India;
};

/** Compare stored E.164 mobile with form country code + local digits (digits-only). */
const phonesMatchForVerification = (
  storedPhoneE164: string | undefined,
  countryCode: string,
  localPhone: string | undefined
): boolean => {
  if (!storedPhoneE164 || !localPhone) {
    return false;
  }
  const storedDigits = storedPhoneE164.replace(/\D/g, "");
  const ccDigits = countryCode.replace(/\D/g, "");
  const localDigits = localPhone.replace(/\D/g, "");
  if (!storedDigits || !localDigits) {
    return false;
  }
  return storedDigits === `${ccDigits}${localDigits}`;
};

function Profile() {

  const navigate = useNavigate();

  const { data: user, isLoading: isLoadinguser, refetch: refetchUser } = useUserFetch();
  const { data: capabilities, isLoading: isLoadingCapabilities } = useCapabilitiesFetch();
  const { data: integrations, isLoading: isLoadingIntegrations } = useIntegrationsQuery();
  const { mutate: updateUser, isPending: isUserPending, status: isUserStatus, error: userErrorMsg } = useUserUpdate();
  const { mutate: updatePassword, isPending: isPasswordPending, status: isPasswordStatus, error: passwordErrorMsg  } = usePasswordUpdate() as any;
  const { data: capabilitiesActionNeeded } = useCapabilitiesActionNeededFetch();
  const { data: activatedKeys, isLoading: actionKeysLoading} = useActivatedKeysFetch();
  const { mutate: sendSmsVerification, isPending: isSendingSms, status: smsStatus } = useSmsVerificationSend();
  const { data: phoneStatus, refetch: refetchPhoneVerificationStatus } = usePhoneVerificationStatus() as any;
  const { trackProfileClicked } = useProfileAdoption();

  const userAvatar = _.get(user, 'icon.full', '');

  const enabledIntegrations = _.filter(capabilities, (capability) => capability?.enabled);
  const mobileVerificationCapability = useMemo(() => {
    return _.find(capabilities, (capability) => {
      const capabilityType = _.toUpper(_.get(capability, "productCapabilityResponse.type", ""));
      const capabilityName = _.toLower(_.get(capability, "productCapabilityResponse.name", ""));

      return (
        capabilityType === "PHONE_VERIFICATION" ||
        capabilityType === "SMS_VERIFICATION" ||
        capabilityType === "MOBILE_VERIFICATION" ||
        capabilityName.includes("mobile")
      );
    });
  }, [capabilities]);

  const mobileVerificationSummary = useMemo(() => {
    if (!mobileVerificationCapability) return null;

    const totalUsers = _.toNumber(mobileVerificationCapability.totalUsers) || 0;
    const verifiedUsers = _.clamp(_.toNumber(mobileVerificationCapability.count) || 0, 0, totalUsers);
    const pendingUsers = Math.max(totalUsers - verifiedUsers, 0);

    return {
      totalUsers,
      verifiedUsers,
      pendingUsers,
    };
  }, [mobileVerificationCapability]);
 
  const notActivatedAndEnabledIntegrations: IntegrationCapability[] = useMemo(() => {
    return _.filter(enabledIntegrations, (capability) => {
      const type = capability?.productCapabilityResponse?.type;
      return (
        capability?.productCapabilityResponse?.category?.type === "USER_SPECIFIC" &&
        !activatedKeys?.includes(type) &&
        capability?.productCapabilityResponse?.integration
      );
    });
  }, [activatedKeys]); // dependency 

  const [selectedCapablitites, setSelectedCapablities] = useState<IntegrationCapability[]>([]);
  const [showUpdateProfileSlider, setShowUpdateProfileSlider] = useState<boolean>(false);
  const [showUpdatePasswordSlider, setShowUpdatePasswordSlider] = useState<boolean>(false);
  const [manageProfile, setManageProfile] = useState<profileDetails | null>(null);
  const [managePassword, setManagePassword] = useState<resetPassword>(defaultManagePassword)
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});
  const [showDownloadDeviceSlider, setShowDownloadDeviceSlider] = useState<boolean>(false);
  const [selectedDownloadDevice, setSelectedDownloadDevice] = useState<IntegrationCapability | null>(null)
  const [showimageEditor, setShowImageEditor] = useState<boolean>(false);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const mobilePhoneInputRef = useRef<HTMLInputElement>(null);
  const [showInValidEmailSlider, setShowInValidEmailSlider] = useState<boolean>(false);
  const [selectedEmailIntegration, setSelectedEmailIntegration] = useState<string | null>(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCodeEnum>(CountryCodeEnum.India);
  /** Snapshot when Manage profile opens — restored on Cancel */
  const profileFormSnapshotRef = useRef<{
    countryCode: CountryCodeEnum;
    manageProfile: profileDetails | null;
  } | null>(null);
  const prevProfileSliderOpenRef = useRef(false);
  /** After OTP is sent successfully, lock Verify until verified or number changes */
  const [otpSentCompositeKey, setOtpSentCompositeKey] = useState<string | null>(
    () => getPhoneVerificationPending()
  );

  const countryCodeOptions = [
    { code: CountryCodeEnum.India, label: '(India)' },
    { code: CountryCodeEnum.UnitedStates, label: '(United States)' }
  ];

  const isStarterPlan = subscriptionPlanService.isStarterPlan();
  const isCoachingEnabled = _.get(userRoleService.account, 'coachingEnabled', false);
  
  /* Methods */
  useEffect(() => { // Mount
    trackProfileClicked(); // event
  },[]);

  useEffect(() => {
    if(user){
      const detectedCountryCode = detectCountryCodeFromPhone(user.mobilePhone);
      const updatedUserObj = user.mobilePhone
        ? { ...user, mobilePhone: stripCountryCode(user.mobilePhone) }
        : user;

      setSelectedCountryCode(detectedCountryCode);
      setManageProfile(updatedUserObj);
    }
  },[user])

  // Capture form state when Manage profile slider opens (for Cancel restore)
  useEffect(() => {
    if (showUpdateProfileSlider && !prevProfileSliderOpenRef.current) {
      profileFormSnapshotRef.current = {
        countryCode: selectedCountryCode,
        manageProfile: manageProfile ? { ...manageProfile } : null,
      };
    }
    prevProfileSliderOpenRef.current = showUpdateProfileSlider;
    // Intentionally only keyed on slider open/close — snapshot values from that render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpdateProfileSlider]);

  useEffect(() => {  
    if(_.size(capabilities) > 0 && _.size(integrations) > 0) {
     let enableUserCapablities = _.filter (capabilities, (capability) =>  capability.enabled && capability.productCapabilityResponse?.category?.type  === 'USER_SPECIFIC'); 
     if(user && !user.admin){ // remove some stacks,
       enableUserCapablities = _.filter(enableUserCapablities, (capability) => !adminOnlyCapabilityTypes.includes(capability.productCapabilityResponse.type));
     };
     if(_.size(enableUserCapablities) > 0) {
        setSelectedCapablities(() =>
          _.map(enableUserCapablities, (capability) => {
            const type = capability.productCapabilityResponse.type;

            if(_.includes(activatedKeys, type)) { // completed
                capability.status = 'activated'; 
            } else { // pending
                capability.status = 'deActivated';
            }
            
            return capability;
          })
         )
        return;
      };
    }
    setSelectedCapablities([]); // empty
  }, [capabilities,integrations, activatedKeys]);

  useEffect(() => {
    if(showUpdateProfileSlider || showimageEditor){  // user
        if(isUserStatus === 'success'){
          setShowUpdateProfileSlider(false);
          setShowImageEditor(false);
          refetchPhoneVerificationStatus?.();
          if (inputFileRef.current) {
             inputFileRef.current.value = ""; // clear after upload
          }
          toast(
              <Toast.Sucess
                        message="Updated Sucessfully"
                    />, {
                    autoClose: 3000,
          })
        } else if(isUserStatus === 'error') {
            toast(
              <Toast.Failure
                        message={`error in updating profile`}
                    />, {
                autoClose: 3000,
            });
        }
    }

    if(showUpdatePasswordSlider){ // password
       if(isPasswordStatus === 'success'){
          setShowUpdatePasswordSlider(false);
          toast(
           <Toast.Sucess
                        message="Updated Sucessfully"
                    />, {
                    autoClose: 3000,
          })
      } else if(isPasswordStatus === 'error') {
          if(passwordErrorMsg && _.size(passwordErrorMsg) > 0){
            _.forEach(passwordErrorMsg.errors,
              (err) => { 
                setErrorMessages((prev) => ({...prev, 
                   ...((err.code === "password.old.match" || err.code === "password.previous.match") && {'oldPassword':  `${err.message}`}),
                   ...((err.code === "password.new.match" || err.code === "password.invalid.length") && {'newPassword1':  `${err.message}`})
                }));
            });
          };
      } 
     }
  }, [isUserStatus, isPasswordStatus])


  // profile Details
  const handleChangeProfile = (e: React.ChangeEvent<HTMLInputElement>) => { // field change
     const { name: key, value} = e.target;
     
     // For mobile phone, only allow digits
     if (key === ProfileEnum.mobilePhone) {
       // Remove any characters that are not digits
       const digitsOnly = value.replace(/[^\d]/g, '');
       
       // Limit to maximum 10 characters (phone number without country code)
       const limitedValue = digitsOnly.slice(0, 10);
       
       setManageProfile((prev) => ({...prev, [key]: limitedValue}));
       setErrorMessages((prev) => {
         if (!prev[ProfileEnum.mobilePhone]) {
           return prev;
         }
         const next = { ...prev };
         delete next[ProfileEnum.mobilePhone];
         return next;
       });
       // Only clear SMS pending when user enters a different complete number
       const pendingKey = otpSentCompositeKey ?? getPhoneVerificationPending();
       if (pendingKey && limitedValue.length === 10) {
         const pendingDigits = pendingKey.replace(/\D/g, "");
         const newDigits = toFullPhoneDigits(selectedCountryCode, limitedValue);
         if (newDigits !== pendingDigits) {
           setOtpSentCompositeKey(null);
           clearPhoneVerificationPending();
         }
       }
     } else {
       setManageProfile((prev) => ({...prev, [key]: value}));
     }
  }

  const handleSaveProfile = (icon?: string | undefined) => { // submit
    if(manageProfile && manageProfile[ProfileEnum.firstName]){ // check for only firstName
       setErrorMessages({}); //reset
       
       // Validate mobile phone if provided (not empty)
       const phoneNumber = manageProfile[ProfileEnum.mobilePhone];
       if (phoneNumber && phoneNumber.trim() !== '') {

         // Check if phone number is valid (must be exactly 10 digits)
         const isValid = isValidMobileNumber(phoneNumber);

         if (!isValid) {
           // Show error toast only (no field highlighting)
           toast(
             <Toast.Failure
               message="Please enter a valid 10-digit mobile number"
             />, {
               autoClose: 3000,
             }
           );
           return; // Don't save
         }
       }
       
       // Combine country code with mobile number for backend
       const mobilePhoneWithCountryCode = manageProfile[ProfileEnum.mobilePhone] 
         ? `${selectedCountryCode}${manageProfile[ProfileEnum.mobilePhone]}`
         : manageProfile[ProfileEnum.mobilePhone];
       

       const reqBody = {
        [ProfileEnum.email]: manageProfile[ProfileEnum.email],
        [ProfileEnum.firstName]: manageProfile[ProfileEnum.firstName],
        [ProfileEnum.lastName]: manageProfile[ProfileEnum.lastName],
        [ProfileEnum.icon]: icon ?? null,
        [ProfileEnum.mobilePhone]: mobilePhoneWithCountryCode,
        [ProfileEnum.title]: manageProfile[ProfileEnum.title],
        [ProfileEnum.workPhone]: manageProfile[ProfileEnum.workPhone],
       };
       // updating linkedinUrl if and only if it is changed
       if (_.get(user, "linkedinUrl") !== manageProfile.linkedinUrl) {
          reqBody[ProfileEnum.linkedinUrl] = manageProfile.linkedinUrl;
       }

       // Clear SMS pending only when Confirm changes or removes the mobile number.
       // Saving other fields with the same unverified number must keep Verify disabled.
       const pendingKey = otpSentCompositeKey ?? getPhoneVerificationPending();
       if (pendingKey) {
         const pendingDigits = pendingKey.replace(/\D/g, "");
         const savedDigits = mobilePhoneWithCountryCode
           ? mobilePhoneWithCountryCode.replace(/\D/g, "")
           : "";
         if (!savedDigits || savedDigits !== pendingDigits) {
           setOtpSentCompositeKey(null);
           clearPhoneVerificationPending();
         }
       }

       updateUser(reqBody); // api call
    } else {
       setErrorMessages((prev) => ({...prev,[ProfileEnum.firstName]: 'invalid first name'}));
    }
  }

  const handleCloseProfile = () => { //reset
     const snapshot = profileFormSnapshotRef.current;

     if (snapshot?.manageProfile) {
       setSelectedCountryCode(snapshot.countryCode);
       setManageProfile(snapshot.manageProfile);
     } else {
       let updatedUserObj = user;

       if (user?.mobilePhone) {
         updatedUserObj = {
           ...user,
           mobilePhone: stripCountryCode(user.mobilePhone),
         };
       }

       setSelectedCountryCode(detectCountryCodeFromPhone(user?.mobilePhone));
       setManageProfile(updatedUserObj);
     }

     setErrorMessages({});
     // Do not clear SMS pending here — closing the slider (including after Verify succeeds)
     // must keep Verify disabled until the number is verified.
     refetchPhoneVerificationStatus?.();
     setShowUpdateProfileSlider(false);
  }

  const handleOpenProfileForMobile = () => {
    setShowUpdateProfileSlider(true);
    refetchPhoneVerificationStatus?.();
    // Focus on mobile input after slider opens
    setTimeout(() => {
      if (mobilePhoneInputRef.current) {
        mobilePhoneInputRef.current.focus();
        mobilePhoneInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  }

  // Check if the current mobile number is verified and unchanged
  const isMobileNumberVerified = (): boolean => {
    if (!phoneStatus?.phoneVerified) {
      return false;
    }

    const currentPhone = manageProfile?.mobilePhone;

    if (!currentPhone) {
      return false;
    }
    const strippedCurrentPhone = currentPhone.replace(/[^\d]/g, '');

    // Preferred source of truth: verified phone context from status endpoint.
    if (hasMaskedDigits(phoneStatus?.maskedNumber)) {
      return doesMaskedNumberMatchPhone(phoneStatus?.maskedNumber, strippedCurrentPhone);
    }

    // Fallback when mask is absent: compare against persisted user mobile.
    const verifiedPhone = user?.mobilePhone;
    if (!verifiedPhone) {
      return false;
    }
    const strippedVerifiedPhone = stripCountryCode(verifiedPhone);
    return strippedVerifiedPhone === strippedCurrentPhone;
  }

  const mobileVerificationCompositeKey = useMemo(() => {
    const digits = (manageProfile?.mobilePhone ?? "").replace(/\D/g, "");
    return `${selectedCountryCode}${digits}`;
  }, [selectedCountryCode, manageProfile?.mobilePhone]);

  /**
   * Lock Verify only after SMS was sent (not when mobile was saved via Confirm).
   * Match pending against form AND persisted user mobile so tab navigation keeps the lock.
   */
  const isVerifyLockedForCurrentNumber = useMemo(() => {
    if (phoneStatus?.phoneVerified) {
      return false;
    }

    const pendingKey = otpSentCompositeKey ?? getPhoneVerificationPending();
    if (!pendingKey) {
      return false;
    }

    const pendingDigits = pendingKey.replace(/\D/g, "");
    const userDigits = user?.mobilePhone?.replace(/\D/g, "") ?? "";
    const smsPendingOnSavedMobile =
      Boolean(userDigits) && userDigits === pendingDigits;

    const formMobile = manageProfile?.mobilePhone ?? "";
    if (isValidMobileNumber(formMobile)) {
      const formDigits = toFullPhoneDigits(selectedCountryCode, formMobile);
      if (pendingDigits === formDigits) {
        return true;
      }
    }

    // Link sent but user changed country (field cleared) or is re-typing the same number
    if (smsPendingOnSavedMobile) {
      if (!formMobile) {
        return true;
      }
      const pendingLocal = getLocalDigitsForCountry(pendingKey, selectedCountryCode);
      if (
        pendingLocal.startsWith(formMobile) &&
        formMobile.length < pendingLocal.length
      ) {
        return true;
      }
      if (pendingDigits === userDigits) {
        return true;
      }
    }

    return false;
  }, [
    phoneStatus?.phoneVerified,
    otpSentCompositeKey,
    user?.mobilePhone,
    selectedCountryCode,
    manageProfile?.mobilePhone,
  ]);

  useEffect(() => {
    if (phoneStatus?.phoneVerified) {
      setOtpSentCompositeKey(null);
      clearPhoneVerificationPending();
      return;
    }
    const pending = getPhoneVerificationPending();
    if (pending) {
      setOtpSentCompositeKey((current) => current ?? pending);
    }
  }, [phoneStatus?.phoneVerified, user?.mobilePhone]);

  // Refresh when profile mounts again (e.g. after navigating to Notifications and back)
  useEffect(() => {
    refetchUser?.();
    refetchPhoneVerificationStatus?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount only
  }, []);

  /**
   * Poll phone verification status only while waiting for SMS verification for the same number
   * (after Verify succeeded). Do not poll on every open Manage profile modal.
   */
  const PHONE_STATUS_POLL_MS = 15000;

  useEffect(() => {
    if (!isVerifyLockedForCurrentNumber) {
      return;
    }

    const intervalId = window.setInterval(() => {
      refetchPhoneVerificationStatus?.();
    }, PHONE_STATUS_POLL_MS);

    return () => window.clearInterval(intervalId);
  }, [isVerifyLockedForCurrentNumber, refetchPhoneVerificationStatus]);

  const getMobileInputStatus = () => {
    const isVerified = isMobileNumberVerified();
    const isValid = isValidMobileNumber(manageProfile?.mobilePhone);
    return {
      showVerifiedBadge: isVerified,
      showGetLinkButton: !isVerified && isValid,
    };
  }

  const mobileInputStatus = getMobileInputStatus();

  const handleMobileCountryCodeChange = (code: string) => {
    setSelectedCountryCode(code as CountryCodeEnum);
    // Clear mobile number when country code changes (number format differs by country)
    setManageProfile((prev) => ({
      ...prev,
      [ProfileEnum.mobilePhone]: "",
    }));
    // Clear any error messages for mobile phone
    setErrorMessages((prev) => {
      if (!prev[ProfileEnum.mobilePhone]) {
        return prev;
      }
      const next = { ...prev };
      delete next[ProfileEnum.mobilePhone];
      return next;
    });
    // Keep SMS pending — user may re-enter the same number after picking country again
  };

  const isGetOtpButtonDisabled = isSendingSms || isVerifyLockedForCurrentNumber;

  const handleGetVerificationLink = () => {
    const mobileNumber = manageProfile?.mobilePhone ?? "";

    if (!isValidMobileNumber(mobileNumber)) {
      toast(
        <Toast.Failure message="Please enter a valid mobile number" />,
        { autoClose: 3000 }
      );
      return;
    }

    const digitsOnly = mobileNumber.replace(/\D/g, "");
    const mobileNumberWithCountryCode = `${selectedCountryCode}${digitsOnly}`;

    sendSmsVerification(mobileNumberWithCountryCode, {
      onSuccess: (response: SmsVerificationResponse) => {
        setErrorMessages((prev) => {
          if (!prev[ProfileEnum.mobilePhone]) {
            return prev;
          }
          const next = { ...prev };
          delete next[ProfileEnum.mobilePhone];
          return next;
        });
        setOtpSentCompositeKey(mobileNumberWithCountryCode);
        setPhoneVerificationPending(mobileNumberWithCountryCode);
        // Close without handleCloseProfile clearing pending (set show directly)
        setShowUpdateProfileSlider(false);
        refetchUser();
        refetchPhoneVerificationStatus?.();
        toast(
          <Toast.Sucess
            message={response.message || "Verification link sent successfully"}
          />, { autoClose: 3000 }
        );
      },
      onError: (error: unknown) => {
        const { code: errorCode, message: payloadMessage } = extractPaperfliteErrorPayload(error);
        
        // Get user-friendly error message, handling raw error codes
        const fallbackMessage = "Failed to send verification link. Please try again.";
        const userFriendlyMessage = getUserFriendlyErrorMessage(
          errorCode,
          payloadMessage,
          fallbackMessage
        );

        if (isPhoneNumberAlreadyAssignedError(errorCode, payloadMessage)) {
          setErrorMessages((prev) => ({
            ...prev,
            [ProfileEnum.mobilePhone]: userFriendlyMessage,
          }));
          toast(<Toast.Failure message={userFriendlyMessage} />, { autoClose: 5000 });
          return;
        }

        toast(<Toast.Failure message={userFriendlyMessage} />, { autoClose: 3000 });
      }
    });
  }

  // reset Password
  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {// field change
     const { name: key, value} = e.target;
     setManagePassword((prev) => ({...prev, [key]: value}));
  }

  const handleSavePassword = () => { // submit
      if(managePassword[ProfileEnum.oldPassword] && managePassword[ProfileEnum.newPassword1] && managePassword[ProfileEnum.newPassword2]){
        setErrorMessages({}); //reset
        const reqBody = {
          [ProfileEnum.oldPassword]: managePassword[ProfileEnum.oldPassword],
          [ProfileEnum.newPassword1]: managePassword[ProfileEnum.newPassword1],
          [ProfileEnum.newPassword2]: managePassword[ProfileEnum.newPassword2],
        }
        updatePassword(reqBody); // api call
      } else {
          _.forEach(managePassword,
             (value, key) => { 
              if(managePassword[key]){  // remove
                 setErrorMessages((prev) => {
                    let previousValues = prev;
                    delete previousValues[key];
                    return previousValues;
                 })
              } else { // add
                setErrorMessages((prev) => ({...prev,[key]:  `${resetPasswordErrorLabels[key]} is required.`})); 
              }
          });
      }
  }

  const handleClosePassword = () => { // reset
    setManagePassword(defaultManagePassword);
    setErrorMessages({}); 
    setShowUpdatePasswordSlider(false);
  }

  // action needed or integrations
  const handleIntegrationClick = (integration: IntegrationCapability) => {
      const integrationType = integration.productCapabilityResponse?.type;
      if (integrationType) {
        if(integration.productCapabilityResponse.category.name === "EMAIL"){ // Email integrations
          const activatedEmailIntegration = getActivatedEmailIntegration();
          if(activatedEmailIntegration){ // already activated
            if(activatedEmailIntegration !== integration.productCapabilityResponse.type){ // not the activated email integration
              setSelectedEmailIntegration(integration.id);
              setShowInValidEmailSlider(true);
              return true;
            }
          } 
        }
        // Navigate to the integration setup page
        const getPath = getIntegrationPath(integrationType);
        if (getPath) {
          navigate(`/settings/integrations/${getPath.toLowerCase()}?source=profile`);
        }
      }
  }
  
  // applications
  const handleDownloadApplications = (capability: IntegrationCapability) => { 
      setSelectedDownloadDevice(capability);
      setShowDownloadDeviceSlider(!showDownloadDeviceSlider);
  }

  const handleCloseDownloadApplications = () => {
      setSelectedDownloadDevice(null);
      setShowDownloadDeviceSlider(false);
  }

  // links
  const handleExternalLinks = (type: string) => {
    window.open(externalLinksMapping[type], "_blank", "noopener,noreferrer"); // open in new tab
  }

  // render integration status
  const renderIntegrationStatus = (capability: IntegrationCapability) => { 
    const type = capability.productCapabilityResponse.type;
    if(capability.productCapabilityResponse.integration){ // integrations
          return ( <IntegrationStatus 
                    type={CapabilityCategoriesEnum.integration} 
                     enabled={capability.status === 'activated'} /> 
                  )
    } else if(applicationPlatformTypes.includes(type as CapabilityPlatformTypesEnum)) { // applications
          return (
            <IntegrationStatus  
                     type={CapabilityCategoriesEnum.application}  
                     enabled={capability.status === 'activated'} />
          )
    } else if(externalLinkTypes.includes(type as CapabilityPlatformTypesEnum)) { // links
        return <IntegrationStatus  
                  type={CapabilityCategoriesEnum.link}  
                  enabled={capability.status === 'activated'} />
     }
  }

  // profile pic change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
     const file = e.target.files ? e.target.files[0] : null;
     if (!file) return;

     const maxSize = 2 * 1024 * 1024; // 2MB
     
     if (file.size <= maxSize) {
       setShowImageEditor(true);
     } else { // file size exceeds limit
        toast(
              <Toast.Failure
                message="File size exceeds 2MB. Please select a smaller image."
              />,
              {
                autoClose: 2000,
              }
       );
            
       if (inputFileRef.current) { // clear
                inputFileRef.current.value = ""; 
       }
    }  
  }
  
  const handleResetFile = () => { // reset file
      setShowImageEditor(false); 
      if (inputFileRef?.current) { // clear
        inputFileRef.current.value = ""; 
      }
  }

  // Get the activated email integration type
  const getActivatedEmailIntegration = () => {
    const typeOfEmailIntegrations = ['outlook_integration', 'gmail_integration', 'smtp_integration'];
    return _.filter(typeOfEmailIntegrations, type => _.includes(activatedKeys, type))[0];
  }

  // handle capabilities click
  const handleCapabilitiesClick = (capability: IntegrationCapability) => {
    const type = capability.productCapabilityResponse.type;
    if(capability.productCapabilityResponse.integration){ // integrations
          return handleIntegrationClick(capability);
    } else if(applicationPlatformTypes.includes(type as CapabilityPlatformTypesEnum)) { // applications
          return handleDownloadApplications(capability);
    } else if(externalLinkTypes.includes(type as CapabilityPlatformTypesEnum)) { // links
          return handleExternalLinks(type);
     }
  }

  return (
    <>
     { !isLoadinguser && !isLoadingIntegrations && !isLoadingCapabilities && !actionKeysLoading ? <div className={cx(styles.container)}>
      <div className={cx(styles.header)}>
               <div className={cx(styles.headerContent)}>
                  <UserAvatar src={userAvatar} isEdit={true} handleClick={() => inputFileRef.current && inputFileRef.current.click()}/>
                  <div className={cx(styles.headerInfo)}>
                    <p className={cx(styles.name)} >{_.get(user, 'firstName', '')} {_.get(user, 'lastName', '')}</p>
                    <p className={cx(styles.email)}>{_.get(user, 'email')}</p>
                  </div>
               </div>

             <div className={cx(styles.headerActionBtns)}>
               <Actions.Button
                width={3}
                icon={"paperflite-edit"}
                title="edit profile"
                onClick={() => {
                    setShowUpdateProfileSlider(!showUpdateProfileSlider);
                }}
                />

                <Actions.IconButton
                icon={"paperflite-request-access"}
                onClick={() => {
                    setShowUpdatePasswordSlider(!showUpdatePasswordSlider);
                }}
                />
             </div>
        </div>
        <div className={cx(styles.contentContainer)}>
            <div className={cx(styles.integrations)}>
             { _.size(selectedCapablitites) > 0 &&  <StackProgressBar stacks={selectedCapablitites} classnames={styles.stackProgress}/>}
                <div className={cx(styles.integrationsContainer)}>
                   <p className={cx(styles.description)}>Here’s a list of integrations ready for you to set up.</p>
                   <div className={cx(styles.integrationsContent)}>
                        {  _.size(selectedCapablitites) > 0 ? <>  
                            { _.map(selectedCapablitites, (capability: IntegrationCapability) => (
                              <IntegrationCard  
                                    key={capability.id}
                                    type="integration" 
                                    title={capability.productCapabilityResponse.name}
                                    subTitle={capability.productCapabilityResponse.description}
                                    iconUrl={capability.productCapabilityResponse.icon}
                                    className={styles.integrationCard}
                                    status={
                                        renderIntegrationStatus(capability)
                                      }
                                    handleClick={() => handleCapabilitiesClick(capability)  }
                                    />
                           ))}                    
                       </> : <>
                            <IntegrationPlaceholder/>
                            <EmptyIntegration/>
                     </> }
                   </div>
                </div>
            </div>   

            <div className={styles.rightPane}>
             { !isStarterPlan && (_.size(capabilitiesActionNeeded) > 0 || _.size(notActivatedAndEnabledIntegrations) > 0) && 
              <div className={styles.actionNeeded}>
              <p className={styles.title}>action needed</p>

              <div className={styles.innerContainer}>

              {_.size(capabilitiesActionNeeded) > 0 && capabilitiesActionNeeded.map((item: any, index: number) => (
                  <ActionNeeded 
                       key={index} 
                       isIntegrated={true} 
                         classnames={styles.content} 
                       name={item?.productCapabilityResponse?.name} 
                       icon={item?.productCapabilityResponse?.icon}
                       handleClick={() => handleIntegrationClick(item)}
                   />
              ))}

              {_.size(notActivatedAndEnabledIntegrations) > 0 && notActivatedAndEnabledIntegrations?.map((item, index: number) => (
                <ActionNeeded 
                 key={index} 
                 classnames={styles.content} 
                 isIntegrated={false} 
                 name={item?.productCapabilityResponse?.name} icon={item?.productCapabilityResponse?.icon}
                 handleClick={() => handleIntegrationClick(item)}
                />))}
             </div>
            </div>}

               {/* { _.map(newVersionAvailableInfo, (info, itemKey) => (
                  <div key={itemKey} className={styles.versionUpdate}>
                      <p className={styles.title}>new version available</p>
                      <VersionUpdate version={info.version} qrLink={info.qrLink} deviceName={info.deviceName} />
                    </div> 
                ))} */}
 
                <div className={styles.notifications}>
                    <p className={styles.title}>notifications</p>
                    <div>
                      <p className={styles.subText}>Stay up-to-date with real-time updates. Configure your notifications today.</p>
                      <Link className={styles.actionItem} to={'/settings/notifications'}>  <i className='paperflite-right-arrow'/>  Set up now </Link>
                    </div>
                </div>

                  {!isStarterPlan && (<RenderEmailSignature signature={_.get(user,'emailSignature')}/>)}
                  {!isStarterPlan && isCoachingEnabled && (
                    <RenderMobileNumber
                      mobilePhone={_.get(user,'mobilePhone')}
                      onVerifyClick={handleOpenProfileForMobile}
                      accountSummary={mobileVerificationSummary}
                    />
                  )}
            </div>   
        </div> 

      <Slider.Dialog
        width="380px"
        show={showUpdateProfileSlider}
        onClose={handleCloseProfile}
        className={styles.slider}
      >
        <Slider.Header
          title="Manage profile"
          message="Edit your profile information shown when sharing collections." />
        <Slider.Body>
          
           <div className={styles.manageProfile}>
                <InputGroup
                   label="first Name"
                   name={ProfileEnum.firstName}
                   value={manageProfile?.firstName}
                   onChange={handleChangeProfile}
                   errorMessage={_.size(errorMessages) > 0 ? errorMessages[ProfileEnum.firstName] : undefined}
                />

                 <InputGroup
                   label="last Name"
                   name={ProfileEnum.lastName}
                   value={manageProfile?.lastName}
                   onChange={handleChangeProfile}
                />

                 <InputGroup
                   label="title"
                   name={ProfileEnum.title}
                   value={manageProfile?.title}
                   onChange={handleChangeProfile}
                />
                 <InputGroup
                   label="email"
                   name={ProfileEnum.email}
                   value={manageProfile?.email}
                   onChange={handleChangeProfile}
                   disabled={true}
                />

                 <InputGroup
                   label="work phone"
                   name={ProfileEnum.workPhone}
                   value={manageProfile?.workPhone}
                   onChange={handleChangeProfile}
                />

                 {isCoachingEnabled && (
                   <PhoneVerificationInput
                     label="mobile"
                     name={ProfileEnum.mobilePhone}
                     value={manageProfile?.mobilePhone}
                     onChange={handleChangeProfile}
                     ref={mobilePhoneInputRef}
                     errorMessage={errorMessages[ProfileEnum.mobilePhone]}
                     infoIcon={{
                       show: true,
                       tooltip:
                         'By verifying your mobile number, you consent to receive notifications from Paperflite on your mobile device.',
                     }}
                     countryCode={{
                       value: selectedCountryCode,
                       options: countryCodeOptions,
                       onChange: handleMobileCountryCodeChange
                     }}
                     actionButton={mobileInputStatus.showGetLinkButton ? {
                       text: 'Verify',
                       onClick: handleGetVerificationLink,
                       icon: 'paperflite-right-arrow',
                       disabled: isGetOtpButtonDisabled,
                     } : undefined}
                     verifiedBadge={mobileInputStatus.showVerifiedBadge ? {
                       show: true,
                       text: 'verified'
                     } : undefined}
                   />
                 )}

                <InputGroup
                   label="LinkedIn URL"
                   name={ProfileEnum.linkedinUrl}
                   value={_.get(manageProfile, "linkedinUrl", "")}
                   onChange={handleChangeProfile}
                />
           </div>
        </Slider.Body>

        <Slider.Actions>
          <Button variant="primary" icon='paperflite-tick' text={isUserPending ? "Updating..." : "Confirm"}  disabled={isUserPending}  onClick={() => handleSaveProfile()}></Button>
          <Button variant="secondary" text="Cancel" disabled={isUserPending} onClick={handleCloseProfile}></Button>
        </Slider.Actions>
      </Slider.Dialog>


      <Slider.Dialog
        width="380px"
        show={showUpdatePasswordSlider}
        onClose={handleClosePassword}
        className={styles.slider}
      >
        <Slider.Header
          title="Manage password"
          message="Change your account password." />
        <Slider.Body>
           <div className={styles.managePassword}>
                <InputGroup
                   label="old Password"
                   type="password"
                   name={ProfileEnum.oldPassword}
                   value={managePassword.oldPassword}
                   onChange={handleChangePassword}
                   errorMessage={_.size(errorMessages) > 0 ? errorMessages[ProfileEnum.oldPassword] : undefined}
                />

                 <InputGroup
                   label="new Password"
                   type="password"
                   name={ProfileEnum.newPassword1}
                   value={managePassword.newPassword1}
                   onChange={handleChangePassword}
                   errorMessage={_.size(errorMessages) > 0 ? errorMessages[ProfileEnum.newPassword1] : undefined}
                />

                 <InputGroup
                   label="Re-enter New Password"
                   type="password"
                   name={ProfileEnum.newPassword2}
                   value={managePassword.newPassword2}
                   onChange={handleChangePassword}
                   errorMessage={_.size(errorMessages) > 0 ? errorMessages[ProfileEnum.newPassword2] : undefined}
                />
           </div>
        </Slider.Body>

        <Slider.Actions>
          <Button variant="primary" icon='paperflite-tick' text={isPasswordPending ? "Updating..." : "Confirm"}  disabled={isPasswordPending}  onClick={handleSavePassword}></Button>
          <Button variant="secondary" text="Cancel" disabled={isPasswordPending} onClick={handleClosePassword}></Button>
        </Slider.Actions>
      </Slider.Dialog>

        <Slider.Dialog
        width="380px"
        show={showDownloadDeviceSlider}
        className={styles.downloadDevices}
        onClose={handleCloseDownloadApplications}
      >
         <Slider.Header
          title={
          <div className={styles.headerContainer}>
              <h2 className={styles.header}>Download Device</h2>
              <button className={styles.closeButton} onClick={handleCloseDownloadApplications}>
                <i className="paperflite-close" />
              </button>
          </div>
         }
         message="Get Paperflite on your mobile device."/>
        <Slider.Body>
           { selectedDownloadDevice &&
            <div className={styles.innerContainer}> 
                <p className={styles.title}> {selectedDownloadDevice.productCapabilityResponse.name} </p>
                <div className={styles.downloadDevicesContent}>
                    <GenerateQRCode value={applicationQRCodeMapping[selectedDownloadDevice.productCapabilityResponse.type]}/>
                    <div>
                        <p className={styles.subText}> scan this QR to download the application.</p>
                    </div>
                </div>
           </div>
         }
        </Slider.Body>
      </Slider.Dialog>


      { showInValidEmailSlider && 
     <SlidePaneView  width="30vw" show={showInValidEmailSlider}>
       <IntegrationValidateView 
        activatedIntegration={_.find(integrations, { key: getActivatedEmailIntegration() })}
        clickedIntegration={_.find(selectedCapablitites, { id: selectedEmailIntegration })}
        onCancel={() => {
          setShowInValidEmailSlider(false);
          setSelectedEmailIntegration(null);
        }} 
       />
       </SlidePaneView>
       }

       { showimageEditor && inputFileRef?.current?.files?.[0] && 
         <ImageEditor 
          file={inputFileRef?.current?.files[0]} 
          onClose={() => handleResetFile()}
          onUploadClick={(val) => handleSaveProfile(val)}
          />}

        {/* Upload */}
         <input className={styles.profileUpload} accept="image/*" ref={inputFileRef} type="file" onChange={handleFileChange}/>
       </div> : 
       <div className={cx(styles.loading)}>
            <p>Loading...</p>
       </div> }
    </>
    
  );
}

export const EmptyIntegration = () => {
    return (
       <div className={cx(styles.emptyIntegration)}>
            <img className={cx(styles.noIntegrationsImg)} src={NoIntegrations} alt="no Integrations" />
            <p className={cx(styles.noIntegrationsText)}>No integrations to implement yet!</p>
        </div>
    )
}

export const IntegrationStatus = ({type, enabled, onClick}: {type: CapabilityCategoriesEnum ,enabled: boolean, onClick?: () => void}  ) => {
    return (
      <>
          <button className={styles.integrationStatus} onClick={onClick}>
            { type === CapabilityCategoriesEnum.integration ?
               <>
             <i className={cx(( enabled ? 'paperflite-tick' : 'paperflite-right-arrow'),styles.icon)} /> 
             {!enabled  && 'open integration' }
              </>
               : 
                type === CapabilityCategoriesEnum.application ? 
                   <>
                     <i className={cx(( enabled ? 'paperflite-tick' : 'paperflite-right-arrow'),styles.icon)} />
                      {!enabled && 'download' }
                   </> : 
                   <> 
                   <i className={cx(( enabled ? 'paperflite-tick' : 'paperflite-right-arrow'),styles.icon)} />
                      {!enabled && 'open link' }
                  </>
             }
          </button>
       </>
    )
}

export const IntegrationPlaceholder = () => {
    return (
        <div className={cx(styles.integrationPlaceholder)}>
            <div className={cx(styles.circle)}></div>
            <div className={cx(styles.hr)}></div>
        </div>
    )
}

export const RenderEmailSignature = ({signature} :{signature: string | undefined}) => {

  const { mutate: updateEmailSign, isPending: isEmailSignPending, status: isEmailSignStatus, error: emailSignatureError } = useEmailSignatureUpdate();
  const { trackEmailSignatureAdded } = useProfileAdoption();
  const dispatch = useDispatch(); // dispatch

  const {Renderer: EmailSignatureViewer , TinyMce: EmailSignatureEditor , output: updatedEmailSignature, reset: resetEmailSignature } = useTinyMce(signature ?? '');

  const [showEmailSignatureSlider, setShowEmailSignatureSlider] = useState<boolean>(false);

  /* Methods */
  useEffect(() => {
     if(showEmailSignatureSlider){ // email signature
        if(isEmailSignStatus === 'success') {
          trackEmailSignatureAdded(); // event
          setShowEmailSignatureSlider(false);
          dispatch(fetchUserProfile()); // to reflect the changes in store
          toast(
              <Toast.Sucess
                        message="Updated Sucessfully"
                    />, {
                    autoClose: 3000,
          })
        } else if (isEmailSignStatus === 'error') {
            toast(
              <Toast.Failure
                        message={`error in updating email signature`}
                    />, {
                autoClose: 3000,
            });
        }
     }
  }, [isEmailSignStatus])

  const handleChangeEmailSignature = () => {
   const reqBody ={
    emailSignature: updatedEmailSignature,
   }
   updateEmailSign(reqBody);
  }

  const handleCloseEmailSignature = () => { // reset
    setShowEmailSignatureSlider(false);
    resetEmailSignature();
  }
  
  return ( 
    <>
     <div className={styles.emailSignature}>
              <p className={styles.title}>email signature</p>
              <div className={styles.contentWrapper}>
                      <p className={styles.content}>
                        { !signature ? <i className={cx('paperflite-danger', styles.icon )}/> 
                           : <i className={cx('paperflite-tick-selected', styles.icon)}/>}
                        { !signature ? 'Email signature is pending.' : 'Email signature has been successfully added.'}
                      </p>
                      <p
                         className={styles.action}
                         onClick={() => setShowEmailSignatureSlider(!showEmailSignatureSlider)}>
                          <i className="paperflite-right-arrow" /> { !signature ? 'Add' : 'Update'} Signature
                      </p>
              </div>
              { !signature && <img className={styles.emptyEmailSignature} src={emptySignature} alt="empty Signature"/> }                       
      </div>

    <Slider.Dialog
        width="auto"
        show={showEmailSignatureSlider}
        onClose={handleCloseEmailSignature}
        className={styles.slider}
      >
        <Slider.Header
          title="Email signature"
          message="Create a professional email signature that appears in all your outgoing emails and templates." />
        <Slider.Body>
           <div className={styles.updateEmailSignature}>
                <div className={styles.preview}>
                      <img src={BrowserControlBar} alt="" />
                      <div className={styles.compose}>
                        <div>
                            <p className={styles.composeHeader}> Compose</p>
                           <p className={styles.composeContent}>quick send</p>
                        </div>
                        <div className={styles.innerContainer}>
                              <div className={styles.input}>
                                      <p className={styles.preText}>To</p>
                              </div>
                               <div className={styles.input}>
                                      <p className={styles.preText}>Subject</p>
                              </div>
                        </div>

                        <div>
                               <p className={styles.composeContent}> Hello there,
                                <br/>
                                  This is a sample email created for preview purposes, intended to <br/> demonstrate formatting, layout, and overall presentation.
                                  <br/> <br/>
                                Thank you!
                              </p>
                        </div>

                        <div className={styles.previewSignature}>
                          <div className={styles.restrictEvents}>
                                 <EmailSignatureViewer/>
                          </div>
                        </div>
                      </div>
                </div>
                <div className={styles.editor}>
                   <p>Insert your custom signature here, This is a default feature in all outgoing emails or new email templates.</p>
                  <EmailSignatureEditor classnames={styles.editSignature}/> 
                </div>
           </div>
        </Slider.Body>

        <Slider.Actions>
          <Button variant="primary" icon='paperflite-tick'  text={isEmailSignPending ? "Updating..." : "Confirm"}  disabled={isEmailSignPending}  onClick={handleChangeEmailSignature}></Button>
          <Button variant="secondary" text="Cancel" disabled={isEmailSignPending} onClick={handleCloseEmailSignature}></Button>
        </Slider.Actions>
      </Slider.Dialog>

    </>

  )
}

export const RenderMobileNumber = ({
  mobilePhone,
  onVerifyClick,
  accountSummary,
}: {
  mobilePhone: string | undefined,
  onVerifyClick: () => void,
  accountSummary?: {
    totalUsers: number;
    verifiedUsers: number;
    pendingUsers: number;
  } | null,
}) => {

  const { data: phoneStatus, isLoading: isLoadingStatus } = usePhoneVerificationStatus();

  const isVerified =
    !!phoneStatus?.phoneVerified &&
    doesMaskedNumberMatchPhone(phoneStatus?.maskedNumber, mobilePhone);
  const maskedNumber = phoneStatus?.maskedNumber || mobilePhone;
  const hasAccountSummary = !!accountSummary && accountSummary.totalUsers > 0;

  if (isLoadingStatus) {
    return (
      <div className={styles.mobileNumber}>
        <p className={styles.title}>mobile number</p>
        <div className={styles.contentWrapper}>
          <p className={styles.content}>Loading...</p>
        </div>
      </div>
    );
  }
  
  return ( 
     <div className={styles.mobileNumber}>
              <p className={styles.title}>mobile number</p>
              <div className={styles.contentWrapper}>
                      <p className={styles.content}>
                        { hasAccountSummary ? (
                          <>
                            <i className={cx(
                              accountSummary.pendingUsers > 0 ? 'paperflite-danger' : 'paperflite-tick-selected',
                              styles.icon
                            )}/>
                            {`${accountSummary.verifiedUsers} of ${accountSummary.totalUsers} users have verified mobile numbers.`}
                          </>
                        ) : (
                          <>
                            { !isVerified ? <i className={cx('paperflite-danger', styles.icon )}/>
                              : <i className={cx('paperflite-tick-selected', styles.icon)}/>}
                            { !isVerified ? 'mobile number verification pending' : 'Mobile number has been successfully verified.'}
                          </>
                        )}
                      </p>

                      <div className={styles.actionRow}>
                        {hasAccountSummary ? (
                          <p className={styles.action}>
                            <i className="paperflite-right-arrow" /> Notify all
                          </p>
                        ) : (
                          <>
                            {!isVerified ? (
                              <p className={styles.action} onClick={onVerifyClick}>
                                <i className="paperflite-right-arrow" /> Verify Number
                              </p>
                            ) : (
                              <p className={styles.action} onClick={onVerifyClick}>
                                <i className="paperflite-right-arrow" /> Update Number
                              </p>
                            )}
                          </>
                        )}
                      </div>
              </div>
              { !isVerified && !hasAccountSummary && <img className={styles.emptyMobileNumber} src={emptyMobileNumber} alt="empty mobile number"/> }
      </div>
  )
}

export const ImageEditor = ({file, onUploadClick, onClose}: {file: any, onUploadClick: (string) => void, onClose: () => void }) => {
  return (
            <AssetIconEditor
                          crop={{
                            x: 0,
                            y: 0,
                            width: 100,
                            height: 100,
                            unit: "%",
                            aspect: 1
                          }}
                          cropType='circle'
                      show={true}
                     image={file}
                              collectioName = {''}
                           onChange={onUploadClick}
                           trackAdoptionMetricsForAddLogoUploadClickedEvent={() => console.log("")}
               onCancel={onClose}
             />
  )
}

export default withErrorBoundary(Profile); 